/**
 * DayEditorPage — full content editor for a single pregnancy day.
 *
 * Route: /content/:audience/day/:dayNumber
 *
 * Fields:
 *   - Title, Description (core content)
 *   - Meditation, Prayer (spiritual)
 *   - Bible Verse + Reference (scripture)
 *   - YouTube URL (video) with live thumbnail preview
 *   - Status (not_started → in_progress → completed → published)
 *
 * Phase 7 will add: audio upload, image upload.
 */

import { useEffect, useCallback, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  ArrowRight,
  Save,
  Copy,
  FileText,
  BookOpen,
  Heart,
  Youtube as YoutubeIcon,
  Clock,
  History,
  Languages,
} from 'lucide-react';

import { useAuth }              from '@/hooks/useAuth';
import { useDayContent, useSaveDayContent } from '@/hooks/useContent';
import { useCopyContent }       from '@/hooks/useCopyContent';
import CopyContentDialog        from '@/components/content/CopyContentDialog';
import { useUnsavedChangesContext }         from '@/contexts/UnsavedChangesContext';
import { canEdit, canCopyContent } from '@/utils/permissionUtils';

import Input           from '@/components/common/Input';
import Button          from '@/components/common/Button';
import LoadingSpinner  from '@/components/common/LoadingSpinner';
import Textarea          from '@/components/content/Textarea';
import FieldSection      from '@/components/content/FieldSection';
import StatusSelector    from '@/components/content/StatusSelector';
import YouTubeField      from '@/components/content/YouTubeField';
import AudioUploadField  from '@/components/content/AudioUploadField';
import ImageUploadField  from '@/components/content/ImageUploadField';
import DayHistoryPanel   from '@/components/content/DayHistoryPanel';

import { AUDIENCE_LABELS } from '@/types/auth.types';
import type { Audience }   from '@/types/auth.types';
import type { ContentStatus } from '@/types/content.types';
import {
  getDayInfo,
  getWeekDayRange,
  getPreviousDay,
  getNextDay,
  isValidDayNumber,
} from '@/config/pregnancyCalendar';
import { isValidYouTubeUrl } from '@/utils/youtubeUtils';
import { formatDisplayDate } from '@/utils/formatUtils';

// ─── Form schema ───────────────────────────────────────────────────────────────

const schema = z.object({
  // English
  title:          z.string().max(200, 'Max 200 characters').nullable().optional(),
  description:    z.string().max(5000, 'Max 5000 characters').nullable().optional(),
  meditation:     z.string().max(3000, 'Max 3000 characters').nullable().optional(),
  prayer:         z.string().max(2000, 'Max 2000 characters').nullable().optional(),
  bibleVerse:     z.string().max(1000, 'Max 1000 characters').nullable().optional(),
  bibleReference: z.string().max(100, 'Max 100 characters').nullable().optional(),
  // Malayalam
  titleMl:          z.string().max(200, 'Max 200 characters').nullable().optional(),
  descriptionMl:    z.string().max(5000, 'Max 5000 characters').nullable().optional(),
  meditationMl:     z.string().max(3000, 'Max 3000 characters').nullable().optional(),
  prayerMl:         z.string().max(2000, 'Max 2000 characters').nullable().optional(),
  bibleVerseMl:     z.string().max(1000, 'Max 1000 characters').nullable().optional(),
  bibleReferenceMl: z.string().max(100, 'Max 100 characters').nullable().optional(),
  // Media + status
  youtubeUrl:     z.string().nullable().optional().refine(
    val => !val || isValidYouTubeUrl(val),
    'Enter a valid YouTube URL (youtube.com or youtu.be)'
  ),
  status: z.enum(['not_started', 'in_progress', 'completed', 'published']),
});

type FormValues = z.infer<typeof schema>;

// ─── Helpers ───────────────────────────────────────────────────────────────────

function emptyString(v: string | null | undefined): string {
  return v ?? '';
}

function nullIfEmpty(v: string | null | undefined): string | null {
  const s = (v ?? '').trim();
  return s === '' ? null : s;
}

// ─── DayEditorPage ─────────────────────────────────────────────────────────────

export default function DayEditorPage() {
  const navigate            = useNavigate();
  const { cmsUser }          = useAuth();
  const { setHasUnsavedChanges, confirmNavigation } = useUnsavedChangesContext();

  const { audience: audParam, dayNumber: dayStr } = useParams<{
    audience: string;
    dayNumber: string;
  }>();

  const audience  = audParam as Audience;
  const dayNumber = parseInt(dayStr ?? '0', 10);
  const dayInfo   = getDayInfo(dayNumber);

  // Validate params
  const isValidDay = isValidDayNumber(dayNumber) && !!dayInfo;
  const audienceLabel = AUDIENCE_LABELS[audience] ?? audience;

  // Navigation
  const prevDay = getPreviousDay(dayNumber);
  const nextDay = getNextDay(dayNumber);

  const goToDay = (day: number) => {
    confirmNavigation(() => navigate(`/content/${audience}/day/${day}`));
  };

  const goBackToContent = () => {
    confirmNavigation(() => navigate(`/content/${audience}`));
  };

  // Permission check
  const editable   = cmsUser ? canEdit(cmsUser, audience)        : false;
  const canCopy    = cmsUser ? canCopyContent(cmsUser, audience) : false;

  // Language tab state
  const [language, setLanguage] = useState<'en' | 'ml'>('en');

  // Copy dialog state
  const [copyOpen, setCopyOpen] = useState(false);
  const { mutateAsync: doCopy, isPending: isCopying } = useCopyContent();

  // Load existing content
  const { data: existing, isLoading } = useDayContent(audience, dayNumber);
  const { mutateAsync: save, isPending: isSaving } = useSaveDayContent();

  // Media state — managed outside the text form so uploads persist immediately
  const [audioUrl,         setAudioUrl]         = useState<string | null>(null);
  const [audioStoragePath, setAudioStoragePath] = useState<string | null>(null);
  const [imageUrl,         setImageUrl]         = useState<string | null>(null);
  const [imageStoragePath, setImageStoragePath] = useState<string | null>(null);

  // Persist a single media field change to Firestore immediately
  const saveMediaFields = useCallback(async (fields: Record<string, string | null>) => {
    if (!cmsUser) return;
    await save({
      audience,
      dayNumber,
      isNew:  existing === null,
      input:  fields,
    });
  }, [cmsUser, save, audience, dayNumber, existing]);

  // ─── Form ─────────────────────────────────────────────────────────────

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      title:          '',
      description:    '',
      meditation:     '',
      prayer:         '',
      bibleVerse:     '',
      bibleReference: '',
      titleMl:          '',
      descriptionMl:    '',
      meditationMl:     '',
      prayerMl:         '',
      bibleVerseMl:     '',
      bibleReferenceMl: '',
      youtubeUrl:     '',
      status:         'not_started',
    },
  });

  // Seed media state from Firestore when data loads
  useEffect(() => {
    if (existing) {
      setAudioUrl(existing.audioUrl ?? null);
      setAudioStoragePath(existing.audioStoragePath ?? null);
      setImageUrl(existing.imageUrl ?? null);
      setImageStoragePath(existing.imageStoragePath ?? null);
    }
  }, [existing]);

  // Populate form when data loads
  useEffect(() => {
    if (existing !== undefined) {
      reset({
        title:          emptyString(existing?.title),
        description:    emptyString(existing?.description),
        meditation:     emptyString(existing?.meditation),
        prayer:         emptyString(existing?.prayer),
        bibleVerse:     emptyString(existing?.bibleVerse),
        bibleReference: emptyString(existing?.bibleReference),
        titleMl:          emptyString(existing?.titleMl),
        descriptionMl:    emptyString(existing?.descriptionMl),
        meditationMl:     emptyString(existing?.meditationMl),
        prayerMl:         emptyString(existing?.prayerMl),
        bibleVerseMl:     emptyString(existing?.bibleVerseMl),
        bibleReferenceMl: emptyString(existing?.bibleReferenceMl),
        youtubeUrl:     emptyString(existing?.youtubeUrl),
        status:         existing?.status ?? 'not_started',
      });
    }
  }, [existing, reset]);

  // Sync dirty state with global unsaved-changes context
  useEffect(() => {
    setHasUnsavedChanges(isDirty);
    return () => setHasUnsavedChanges(false);
  }, [isDirty, setHasUnsavedChanges]);

  // Warn before browser close/reload when dirty
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // ⌘S / Ctrl+S — save without clicking the button
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        if (isDirty && editable && !isSaving) {
          handleSubmit(onSubmit)();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // handleSubmit / onSubmit are stable refs from react-hook-form
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty, editable, isSaving]);

  // Watch values for character counters and "hasContent" dots
  const watchedValues = watch();

  // ─── Submit ───────────────────────────────────────────────────────────

  const onSubmit = async (values: FormValues) => {
    try {
      await save({
        audience,
        dayNumber,
        isNew: existing === null,
        input: {
          title:          nullIfEmpty(values.title),
          description:    nullIfEmpty(values.description),
          meditation:     nullIfEmpty(values.meditation),
          prayer:         nullIfEmpty(values.prayer),
          bibleVerse:     nullIfEmpty(values.bibleVerse),
          bibleReference: nullIfEmpty(values.bibleReference),
          titleMl:          nullIfEmpty(values.titleMl),
          descriptionMl:    nullIfEmpty(values.descriptionMl),
          meditationMl:     nullIfEmpty(values.meditationMl),
          prayerMl:         nullIfEmpty(values.prayerMl),
          bibleVerseMl:     nullIfEmpty(values.bibleVerseMl),
          bibleReferenceMl: nullIfEmpty(values.bibleReferenceMl),
          youtubeUrl:     nullIfEmpty(values.youtubeUrl),
          status:         values.status,
        },
      });
      toast.success('Content saved');
      // Reset dirty state without changing values
      reset(values);
    } catch (err) {
      toast.error('Failed to save — please try again');
      console.error(err);
    }
  };

  // ─── Invalid day ──────────────────────────────────────────────────────

  if (!isValidDay) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4 text-center">
        <p className="text-sm font-medium text-surface-700">Day {dayNumber} doesn't exist.</p>
        <Button variant="secondary" onClick={() => navigate(`/content/${audience}`)}>
          Back to {audienceLabel}
        </Button>
      </div>
    );
  }

  // ─── Loading ──────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // ─── Render ───────────────────────────────────────────────────────────

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <div className="space-y-5">

        {/* ── Breadcrumb + day navigation ───────────────────────────── */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <nav className="flex items-center gap-2 text-sm text-surface-500 flex-wrap">
            <button type="button" onClick={goBackToContent}
              className="hover:text-surface-900 transition-colors"
            >
              {audienceLabel}
            </button>
            <span className="text-surface-300">/</span>
            <span>Month {dayInfo.monthNumber}</span>
            <span className="text-surface-300">/</span>
            <span>Week {dayInfo.weekNumber}</span>
            <span className="text-surface-300">/</span>
            <span className="font-semibold text-surface-900">Day {dayNumber}</span>
          </nav>

          {/* Prev / Next day */}
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => prevDay && goToDay(prevDay)}
              disabled={!prevDay}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium
                         text-surface-500 hover:text-surface-900 hover:bg-surface-100
                         disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Day {prevDay ?? '—'}
            </button>
            <button
              type="button"
              onClick={() => nextDay && goToDay(nextDay)}
              disabled={!nextDay}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium
                         text-surface-500 hover:text-surface-900 hover:bg-surface-100
                         disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              Day {nextDay ?? '—'}
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        {/* ── Page header ────────────────────────────────────────────── */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-xl font-bold text-surface-900">
              Day {dayNumber} — {audienceLabel}
            </h2>
            <p className="text-sm text-surface-500 mt-0.5">
              Month {dayInfo.monthNumber} · Week {dayInfo.weekNumber} · {getWeekDayRange(dayInfo.weekNumber)}
            </p>
          </div>

          {/* Save actions */}
          <div className="flex items-center gap-3">
            {isDirty && (
              <span className="flex items-center gap-1.5 text-xs text-amber-600 font-medium">
                <Clock className="h-3.5 w-3.5" />
                Unsaved changes
              </span>
            )}
            {!editable && (
              <span className="text-xs text-surface-400 font-medium">View only</span>
            )}
            {canCopy && (
              <Button
                type="button"
                variant="secondary"
                leftIcon={<Copy className="h-4 w-4" />}
                onClick={() => setCopyOpen(true)}
                disabled={isCopying}
              >
                Copy to…
              </Button>
            )}
            {editable && (
              <Button
                type="submit"
                loading={isSaving}
                disabled={!isDirty}
                leftIcon={<Save className="h-4 w-4" />}
              >
                Save
              </Button>
            )}
          </div>
        </div>

        {/* ── Status + metadata strip ────────────────────────────────── */}
        <div className="flex items-center justify-between gap-4 flex-wrap
                        rounded-xl border border-surface-200 bg-white px-5 py-4">
          <div>
            <p className="text-xs font-semibold text-surface-500 uppercase tracking-wide mb-2">Status</p>
            <Controller
              control={control}
              name="status"
              render={({ field }) => (
                <StatusSelector
                  value={field.value as ContentStatus}
                  onChange={field.onChange}
                  disabled={!editable || isSaving}
                />
              )}
            />
          </div>

          {existing && (
            <div className="text-right text-xs text-surface-400 space-y-0.5">
              {existing.createdAt && (
                <p>Created {formatDisplayDate(existing.createdAt)}
                  {existing.createdBy ? ` by ${existing.createdBy.displayName}` : ''}
                </p>
              )}
              {existing.updatedAt && (
                <p>Updated {formatDisplayDate(existing.updatedAt)}
                  {existing.updatedBy ? ` by ${existing.updatedBy.displayName}` : ''}
                </p>
              )}
            </div>
          )}
        </div>

        {/* ── Language tab toggle ────────────────────────────────────── */}
        <div className="flex items-center gap-2">
          <Languages className="h-4 w-4 text-surface-400" />
          <div className="flex gap-1 p-1 rounded-lg bg-surface-100 w-fit">
            {([
              { key: 'en', label: 'English' },
              { key: 'ml', label: 'മലയാളം' },
            ] as const).map(({ key, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => setLanguage(key)}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                  language === key
                    ? 'bg-white shadow-sm text-surface-900'
                    : 'text-surface-500 hover:text-surface-700'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* ── English content ────────────────────────────────────────── */}
        {language === 'en' && (<>

        <FieldSection
          title="Core Content"
          icon={<FileText className="h-4 w-4" />}
          storageKey="core"
          defaultOpen
          hasContent={!!watchedValues.title || !!watchedValues.description}
        >
          <Input
            label="Title"
            placeholder="Day title shown in the app"
            disabled={!editable || isSaving}
            error={errors.title?.message}
            {...register('title')}
          />
          <Textarea
            label="Description"
            placeholder="Main content for this day…"
            rows={6}
            disabled={!editable || isSaving}
            error={errors.description?.message}
            characterCount={watchedValues.description?.length ?? 0}
            maxCharacters={5000}
            {...register('description')}
          />
        </FieldSection>

        <FieldSection
          title="Spiritual Content"
          icon={<Heart className="h-4 w-4" />}
          storageKey="spiritual"
          defaultOpen
          hasContent={!!watchedValues.meditation || !!watchedValues.prayer}
        >
          <Textarea
            label="Meditation"
            placeholder="Guided meditation or reflection…"
            rows={5}
            disabled={!editable || isSaving}
            error={errors.meditation?.message}
            characterCount={watchedValues.meditation?.length ?? 0}
            maxCharacters={3000}
            {...register('meditation')}
          />
          <Textarea
            label="Prayer"
            placeholder="Prayer for this day…"
            rows={4}
            disabled={!editable || isSaving}
            error={errors.prayer?.message}
            characterCount={watchedValues.prayer?.length ?? 0}
            maxCharacters={2000}
            {...register('prayer')}
          />
        </FieldSection>

        <FieldSection
          title="Scripture"
          icon={<BookOpen className="h-4 w-4" />}
          storageKey="scripture"
          defaultOpen={false}
          hasContent={!!watchedValues.bibleVerse || !!watchedValues.bibleReference}
        >
          <Textarea
            label="Bible Verse"
            placeholder="Scripture verse text…"
            rows={4}
            disabled={!editable || isSaving}
            error={errors.bibleVerse?.message}
            characterCount={watchedValues.bibleVerse?.length ?? 0}
            maxCharacters={1000}
            {...register('bibleVerse')}
          />
          <Input
            label="Bible Reference"
            placeholder="e.g. Psalm 139:13–16"
            disabled={!editable || isSaving}
            error={errors.bibleReference?.message}
            hint="Book, chapter and verse range"
            {...register('bibleReference')}
          />
        </FieldSection>

        </>)}

        {/* ── Malayalam content ──────────────────────────────────────── */}
        {language === 'ml' && (<>

        <FieldSection
          title="പ്രധാന ഉള്ളടക്കം"
          icon={<FileText className="h-4 w-4" />}
          storageKey="core-ml"
          defaultOpen
          hasContent={!!watchedValues.titleMl || !!watchedValues.descriptionMl}
        >
          <Input
            label="ശീർഷകം (Title)"
            placeholder="ആപ്പിൽ കാണിക്കുന്ന ദിവസ ശീർഷകം"
            disabled={!editable || isSaving}
            error={errors.titleMl?.message}
            {...register('titleMl')}
          />
          <Textarea
            label="വിവരണം (Description)"
            placeholder="ഈ ദിവസത്തിനുള്ള പ്രധാന ഉള്ളടക്കം…"
            rows={6}
            disabled={!editable || isSaving}
            error={errors.descriptionMl?.message}
            characterCount={watchedValues.descriptionMl?.length ?? 0}
            maxCharacters={5000}
            {...register('descriptionMl')}
          />
        </FieldSection>

        <FieldSection
          title="ആത്മീയ ഉള്ളടക്കം"
          icon={<Heart className="h-4 w-4" />}
          storageKey="spiritual-ml"
          defaultOpen
          hasContent={!!watchedValues.meditationMl || !!watchedValues.prayerMl}
        >
          <Textarea
            label="ധ്യാനം (Meditation)"
            placeholder="നിർദ്ദേശിത ധ്യാനം അല്ലെങ്കിൽ പ്രതിഫലനം…"
            rows={5}
            disabled={!editable || isSaving}
            error={errors.meditationMl?.message}
            characterCount={watchedValues.meditationMl?.length ?? 0}
            maxCharacters={3000}
            {...register('meditationMl')}
          />
          <Textarea
            label="പ്രാർത്ഥന (Prayer)"
            placeholder="ഈ ദിവസത്തിനുള്ള പ്രാർത്ഥന…"
            rows={4}
            disabled={!editable || isSaving}
            error={errors.prayerMl?.message}
            characterCount={watchedValues.prayerMl?.length ?? 0}
            maxCharacters={2000}
            {...register('prayerMl')}
          />
        </FieldSection>

        <FieldSection
          title="തിരുവചനം"
          icon={<BookOpen className="h-4 w-4" />}
          storageKey="scripture-ml"
          defaultOpen={false}
          hasContent={!!watchedValues.bibleVerseMl || !!watchedValues.bibleReferenceMl}
        >
          <Textarea
            label="ബൈബിൾ വചനം (Bible Verse)"
            placeholder="തിരുവചന ഭാഗം…"
            rows={4}
            disabled={!editable || isSaving}
            error={errors.bibleVerseMl?.message}
            characterCount={watchedValues.bibleVerseMl?.length ?? 0}
            maxCharacters={1000}
            {...register('bibleVerseMl')}
          />
          <Input
            label="ബൈബിൾ റഫറൻസ് (Reference)"
            placeholder="ഉദാ: സങ്കീർത്തനം 139:13–16"
            disabled={!editable || isSaving}
            error={errors.bibleReferenceMl?.message}
            hint="പുസ്തകം, അദ്ധ്യായം, വാക്യം"
            {...register('bibleReferenceMl')}
          />
        </FieldSection>

        </>)}

        {/* ── Media ──────────────────────────────────────────────────── */}
        <FieldSection
          title="Media"
          icon={<YoutubeIcon className="h-4 w-4" />}
          storageKey="media"
          defaultOpen={false}
          hasContent={!!watchedValues.youtubeUrl || !!audioUrl || !!imageUrl}
        >
          {/* YouTube */}
          <Controller
            control={control}
            name="youtubeUrl"
            render={({ field }) => (
              <YouTubeField
                value={field.value ?? ''}
                onChange={val => field.onChange(val ?? '')}
                disabled={!editable || isSaving}
                error={errors.youtubeUrl?.message}
              />
            )}
          />

          <div className="border-t border-surface-100 pt-4">
            <AudioUploadField
              audience={audience}
              dayNumber={dayNumber}
              audioUrl={audioUrl}
              audioStoragePath={audioStoragePath}
              disabled={!editable || isSaving}
              onUploadComplete={async (url, storagePath) => {
                setAudioUrl(url);
                setAudioStoragePath(storagePath);
                await saveMediaFields({ audioUrl: url, audioStoragePath: storagePath });
                toast.success('Audio uploaded');
              }}
              onDelete={async () => {
                setAudioUrl(null);
                setAudioStoragePath(null);
                await saveMediaFields({ audioUrl: null, audioStoragePath: null });
                toast.success('Audio removed');
              }}
            />
          </div>

          <div className="border-t border-surface-100 pt-4">
            <ImageUploadField
              audience={audience}
              dayNumber={dayNumber}
              imageUrl={imageUrl}
              imageStoragePath={imageStoragePath}
              disabled={!editable || isSaving}
              onUploadComplete={async (url, storagePath) => {
                setImageUrl(url);
                setImageStoragePath(storagePath);
                await saveMediaFields({ imageUrl: url, imageStoragePath: storagePath });
                toast.success('Image uploaded');
              }}
              onDelete={async () => {
                setImageUrl(null);
                setImageStoragePath(null);
                await saveMediaFields({ imageUrl: null, imageStoragePath: null });
                toast.success('Image removed');
              }}
            />
          </div>
        </FieldSection>

        {/* ── Change history ─────────────────────────────────────────── */}
        <FieldSection
          title="History"
          icon={<History className="h-4 w-4" />}
          storageKey="history"
          defaultOpen={false}
          hasContent={false}
        >
          <DayHistoryPanel audience={audience} dayNumber={dayNumber} />
        </FieldSection>

        {/* ── Bottom save bar ────────────────────────────────────────── */}
        {editable && (
          <div className="flex items-center justify-between pt-2 border-t border-surface-200">
            <div className="flex items-center gap-2">
              {prevDay && (
                <Button type="button" variant="ghost" size="sm"
                  leftIcon={<ArrowLeft className="h-4 w-4" />}
                  onClick={() => goToDay(prevDay)}
                >
                  Day {prevDay}
                </Button>
              )}
            </div>
            <div className="flex items-center gap-3">
              {isDirty && (
                <span className="text-xs text-amber-600 font-medium flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" /> Unsaved changes
                </span>
              )}
              <Button
                type="submit"
                loading={isSaving}
                disabled={!isDirty}
                leftIcon={<Save className="h-4 w-4" />}
              >
                Save content
              </Button>
            </div>
            <div className="flex items-center gap-2">
              {nextDay && (
                <Button type="button" variant="ghost" size="sm"
                  rightIcon={<ArrowRight className="h-4 w-4" />}
                  onClick={() => goToDay(nextDay)}
                >
                  Day {nextDay}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Copy content dialog */}
      <CopyContentDialog
        open={copyOpen}
        sourceAudience={audience}
        dayNumber={dayNumber}
        sourceContent={existing ?? null}
        getTargetContent={_aud => null} // target content not pre-loaded; copyService fetches fresh
        isCopying={isCopying}
        onClose={() => setCopyOpen(false)}
        onCopy={async (targetAudience, fields, conflictStrategy) => {
          try {
            const result = await doCopy({
              sourceAudience: audience,
              targetAudience,
              dayNumber,
              fields,
              conflictStrategy,
            });
            setCopyOpen(false);
            if (result.copied.length > 0) {
              toast.success(
                `Copied ${result.copied.length} field${result.copied.length !== 1 ? 's' : ''} to ${AUDIENCE_LABELS[targetAudience]}`
              );
            } else {
              toast(`Nothing to copy — all selected fields were empty or already filled.`, { icon: 'ℹ️' });
            }
          } catch (err) {
            toast.error('Copy failed — please try again');
            console.error(err);
          }
        }}
      />
    </form>
  );
}
